import { useState } from 'react';
import {
  CheckCircle,
  XCircle,
  Edit3,
  Calendar,
  MessageSquare,
  Clock,
  Trash2,
  User,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { PlatformBadge } from '../common/PlatformBadge';
import { DraftStatusBadge } from '../common/StatusBadge';
import { MediaPreview } from '../common/MediaPreview';
import { useDraftActions } from '../../hooks/useApi';
import { Modal } from '../common/Modal';
import type { Draft } from '../../types';

interface DraftCardProps {
  draft: Draft;
  onAction?: () => void;
  showContentInfo?: boolean;
}

export function DraftCard({ draft, onAction, showContentInfo: _showContentInfo = false }: DraftCardProps) {
  const { approve, reject, schedule, update, deleteDraft, loading } = useDraftActions();
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(draft.textContent);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const canApprove = ['pending', 'draft', 'in_review'].includes(draft.status);
  const canReject = ['pending', 'draft', 'in_review'].includes(draft.status);
  const canEdit = ['pending', 'draft', 'in_review', 'rejected'].includes(draft.status);
  const canSchedule = draft.status === 'approved';

  const handleApprove = async () => {
    try {
      await approve(draft.draftId);
      onAction?.();
    } catch {
      // Error handled in hook
    }
  };

  const handleReject = async () => {
    if (!rejectNotes.trim()) return;
    try {
      await reject(draft.draftId, rejectNotes);
      setShowRejectModal(false);
      setRejectNotes('');
      onAction?.();
    } catch {
      // Error handled in hook
    }
  };

  const handleSaveEdit = async () => {
    try {
      await update(draft.draftId, { textContent: editText });
      setIsEditing(false);
      onAction?.();
    } catch {
      // Error handled in hook
    }
  };

  const handleSchedule = async () => {
    if (!scheduleDate) return;
    try {
      await schedule(draft.draftId, new Date(scheduleDate).toISOString());
      setShowScheduleModal(false);
      onAction?.();
    } catch {
      // Error handled in hook
    }
  };

  const handleDelete = async () => {
    try {
      await deleteDraft(draft.draftId);
      setShowDeleteConfirm(false);
      onAction?.();
    } catch {
      // Error handled in hook
    }
  };

  return (
    <>
      <div className="card mb-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <PlatformBadge platform={draft.platform} />
            <DraftStatusBadge status={draft.status} />
          </div>
          <span className="text-xs text-gray-500">
            Updated {formatDistanceToNow(new Date(draft.updatedAt), { addSuffix: true })}
          </span>
        </div>

        {/* Media preview */}
        {draft.mediaDownloadUrls && draft.mediaDownloadUrls.length > 0 && (
          <div className="mt-3">
            <MediaPreview
              urls={draft.mediaDownloadUrls}
              mediaType={
                draft.mediaUrls?.[0]?.match(/\.(jpg|jpeg|png|gif|webp)(\?|$)/i) ? 'image' : 'video'
              }
              platform={draft.platform}
            />
          </div>
        )}

        {/* Content preview */}
        <div className="mt-3">
          {isEditing ? (
            <div className="space-y-2">
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                rows={4}
                className="input resize-none"
                placeholder="Write your post..."
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSaveEdit}
                  disabled={loading}
                  className="btn-primary text-xs"
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setEditText(draft.textContent);
                  }}
                  className="btn-secondary text-xs"
                >
                  Cancel
                </button>
                <span className="text-xs text-gray-400">
                  {editText.length} characters
                </span>
              </div>
            </div>
          ) : (
            <div className="rounded-lg bg-gray-50 p-3">
              <p className="whitespace-pre-wrap text-sm text-gray-800">
                {draft.textContent}
              </p>
              {/* Hashtags are already included in textContent - no need to render separately */}
            </div>
          )}
        </div>

        {/* Review notes */}
        {draft.reviewNotes && (
          <div className="mt-3 flex items-start gap-2 rounded-lg bg-red-50 p-3">
            <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
            <div>
              <p className="text-xs font-medium text-red-700">Review Notes</p>
              <p className="mt-0.5 text-sm text-red-600">{draft.reviewNotes}</p>
            </div>
          </div>
        )}

        {/* Schedule info */}
        {draft.scheduledAt && (
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-blue-50 p-3">
            <Clock className="h-4 w-4 text-blue-500" />
            <p className="text-sm text-blue-700">
              Scheduled for {format(new Date(draft.scheduledAt), 'MMM d, yyyy h:mm a')}
            </p>
          </div>
        )}

        {/* Created by */}
        {draft.createdByUser?.email && (
          <div className="mt-2 flex items-center gap-1 text-xs text-gray-400">
            <User className="h-3 w-3" />
            Created by {draft.createdByUser.email}
          </div>
        )}

        {/* Action History */}
        {draft.actionHistory && draft.actionHistory.length > 0 && (
          <div className="mt-2 space-y-1">
            {draft.actionHistory.slice(-3).map((entry, i) => (
              <div key={i} className="flex items-center gap-1 text-xs text-gray-400">
                <Clock className="h-3 w-3" />
                <span className="font-medium capitalize">{entry.action}</span>
                {entry.email && <span>by {entry.email}</span>}
                <span>·</span>
                <span>{formatDistanceToNow(new Date(entry.timestamp), { addSuffix: true })}</span>
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {canApprove && (
            <button
              onClick={handleApprove}
              disabled={loading}
              className="btn-success text-xs"
            >
              <CheckCircle className="mr-1 h-3.5 w-3.5" />
              {loading ? 'Approving...' : 'Approve'}
            </button>
          )}
          {canReject && (
            <button
              onClick={() => setShowRejectModal(true)}
              disabled={loading}
              className="btn-danger text-xs"
            >
              <XCircle className="mr-1 h-3.5 w-3.5" />
              Reject
            </button>
          )}
          {canEdit && !isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="btn-secondary text-xs"
            >
              <Edit3 className="mr-1 h-3.5 w-3.5" />
              Edit
            </button>
          )}
          {canSchedule && (
            <button
              onClick={() => setShowScheduleModal(true)}
              className="btn-secondary text-xs"
            >
              <Calendar className="mr-1 h-3.5 w-3.5" />
              Schedule
            </button>
          )}

          {/* Delete button — always visible */}
          <div className="ml-auto">
            {showDeleteConfirm ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={loading}
                  className="btn-secondary text-xs"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={loading}
                  className="btn-danger text-xs"
                >
                  {loading ? 'Deleting...' : 'Confirm Delete'}
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="rounded p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
                title="Delete draft"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      <Modal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title="Reject Draft"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Please provide feedback on why this draft is being rejected.
          </p>
          <textarea
            value={rejectNotes}
            onChange={(e) => setRejectNotes(e.target.value)}
            rows={3}
            className="input resize-none"
            placeholder="What needs to be changed..."
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowRejectModal(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleReject}
              disabled={!rejectNotes.trim() || loading}
              className="btn-danger"
            >
              {loading ? 'Rejecting...' : 'Reject Draft'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Schedule Modal */}
      <Modal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        title="Schedule Draft"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Choose when this draft should be published.
          </p>
          <input
            type="datetime-local"
            value={scheduleDate}
            onChange={(e) => setScheduleDate(e.target.value)}
            min={new Date().toISOString().slice(0, 16)}
            className="input"
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowScheduleModal(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleSchedule}
              disabled={!scheduleDate || loading}
              className="btn-primary"
            >
              {loading ? 'Scheduling...' : 'Schedule'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
